import React from 'react';
import { Space, Tooltip, Button } from 'antd';
import {useTranslation} from 'react-i18next';
import { motion } from 'framer-motion';
import {
  StyledRowActionCell,
  StyledRowActionCellGhostSpace,
  StyledRowActionCellWrapper
} from './StyledExpenseRowActionCell';
import {CheckIcon, CloseIcon} from '../../../../icons';
import { expensesHelpers } from '../../../../utils/helpers';


// Small component to handle showing the status label and animating actions on hover
function ExpenseRowActionCell({ canTakeAction, onApprove, onReject, expense, handleAction }) {
  const [hovered, setHovered] = React.useState(false);
  const wrapperRef = React.useRef(null);
  const [t] = useTranslation('main');

  const statusLabel = expensesHelpers.getExpenseStatusLabel(expense, t);
  const actionsTransition = { duration: 0.18 };

  React.useEffect(() => {
    if (!canTakeAction) return;

    const node = wrapperRef.current;
    if (!node) return;

    // Find closest td and tr to observe hover-related class changes
    const cell = node.closest('td');
    const row = node.closest('tr');

    const checkHover = () => {
      const cellHover = cell && cell.classList && cell.classList.contains('ant-table-cell-row-hover');
      const rowHover = row && row.classList && row.classList.contains('ant-table-row-hover');
      setHovered(Boolean(cellHover || rowHover));
    };

    // Initial state
    checkHover();

    const targets = [];
    if (cell) targets.push(cell);
    if (row && row !== cell) targets.push(row);
    if (targets.length === 0) return undefined;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          checkHover();
          break;
        }
      }
    });

    targets.forEach(t => observer.observe(t, { attributes: true, attributeFilter: ['class'] }));

    // Also add mouse events to respond immediately (in case classes are toggled elsewhere)
    const onOver = () => setHovered(true);
    const onOut = () => setHovered(false);
    targets.forEach(t => {
      t.addEventListener('mouseover', onOver);
      t.addEventListener('mouseout', onOut);
    });

    return () => {
      observer.disconnect();
      targets.forEach(t => {
        t.removeEventListener('mouseover', onOver);
        t.removeEventListener('mouseout', onOut);
      });
    };
  }, [canTakeAction]);

  if (!canTakeAction) {
    return (
      <StyledRowActionCell>
        {statusLabel}
      </StyledRowActionCell>
    );
  }

  return (
    <StyledRowActionCell>
      {/* wrapper keeps layout size stable: reserve space for actions using padding-right */}
      <StyledRowActionCellWrapper ref={wrapperRef}>
        {/* Status label is always in DOM so it reserves space and prevents jumping; we only animate opacity */}
        <motion.div
          key="status"
          initial={false}
          animate={hovered ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: 0.12 }}
          style={{ willChange: 'opacity', display: 'inline-block', whiteSpace: 'nowrap' }}
        >
          <div style={{ pointerEvents: hovered ? 'none' : 'auto' }}>{statusLabel}</div>
        </motion.div>

        {/* Actions are absolutely positioned inside the reserved area and always rendered; we only animate visibility and position to avoid layout shifts */}
        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={hovered && canTakeAction ? { opacity: 1, x: 0 } : { opacity: 0, x: 8 }}
          transition={actionsTransition}
          style={{ position: 'absolute', top: '0%', pointerEvents: hovered && canTakeAction ? 'auto' : 'none' }}
        >
          <StyledRowActionCellGhostSpace>
            <Space direction='horizontal'>
              <Tooltip title={t('reject')}>
                <Button size='small' icon={<CloseIcon />} onClick={(e) => handleAction({ action: onReject, e, expense })} />
              </Tooltip>
              <Tooltip title={t('approve')}>
                <Button size='small' icon={<CheckIcon />} onClick={(e) => handleAction({ action: onApprove, e, expense })} />
              </Tooltip>
            </Space>
          </StyledRowActionCellGhostSpace>
        </motion.div>
      </StyledRowActionCellWrapper>
    </StyledRowActionCell>
  );
}

export default ExpenseRowActionCell;
